import { FormItem, Input, Textarea } from '@vkontakte/vkui';
import { useState } from 'react';
import { t } from '../../i18n';
import { Action, FieldLabel, FormActions, ImageField, SectionTitle } from '../../shared/ui';

/**
 * Форма питомца — общая для «завести» и «править».
 *
 * Блоки те же, что у предмета и способности: текст, потом фото, потом
 * действия. Фотографий здесь две, и они собраны в один блок намеренно —
 * порознь паспорт читался случайной добавкой к снимку.
 */

export interface PetFormValues {
  name: string;
  species: string;
  description: string;
  imageId: string | null;
  passportImageId: string | null;
}

export const EMPTY_PET: PetFormValues = {
  name: '',
  species: '',
  description: '',
  imageId: null,
  passportImageId: null,
};

export interface PetFormProps {
  initial: PetFormValues;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: PetFormValues) => void;
  onCancel: () => void;
}

export function PetForm({ initial, submitLabel, busy, onSubmit, onCancel }: PetFormProps) {
  const [values, setValues] = useState<PetFormValues>(initial);
  const patch = (next: Partial<PetFormValues>) => setValues((prev) => ({ ...prev, ...next }));

  // Кличка — единственное обязательное поле: колонка name не допускает пустоты.
  const blocker = values.name.trim().length === 0 ? t('pet.form.blocker') : null;

  const canSubmit = blocker === null && busy !== true;

  return (
    <div>
      <SectionTitle>{t('pet.form.section.about')}</SectionTitle>

      <FormItem top={<FieldLabel required>{t('common.field.nickname')}</FieldLabel>}>
        <Input
          value={values.name}
          disabled={busy}
          onChange={(event) => patch({ name: event.target.value })}
        />
      </FormItem>

      <FormItem
        top={<FieldLabel hint={t('pet.form.species.hint')}>{t('common.field.kind')}</FieldLabel>}
      >
        <Input
          value={values.species}
          disabled={busy}
          onChange={(event) => patch({ species: event.target.value })}
        />
      </FormItem>

      <FormItem top={<FieldLabel>{t('common.field.description')}</FieldLabel>}>
        <Textarea
          rows={3}
          value={values.description}
          disabled={busy}
          onChange={(event) => patch({ description: event.target.value })}
        />
      </FormItem>

      <SectionTitle>{t('pet.form.section.photos')}</SectionTitle>

      {/* Подпись у поля своя, вторым заголовком сверху её не дублируем. */}
      <FormItem top={<FieldLabel>{t('pet.form.photo.label')}</FieldLabel>}>
        <ImageField
          label={t('pet.form.photo.label')}
          hint={t('pet.form.photo.hint')}
          imageId={values.imageId}
          onChange={(imageId) => patch({ imageId })}
          disabled={busy}
        />
      </FormItem>

      <FormItem top={<FieldLabel>{t('pet.form.passport.label')}</FieldLabel>}>
        <ImageField
          label={t('pet.form.passport.label')}
          hint={t('pet.form.passport.hint')}
          imageId={values.passportImageId}
          onChange={(passportImageId) => patch({ passportImageId })}
          disabled={busy}
        />
      </FormItem>

      {/* В строку, как в макете: главное действие тянется, отказ от него
          стоит по содержимому. */}
      <FormActions>
        <Action
          tone="action"
          size="l"
          stretched
          loading={busy}
          disabled={!canSubmit}
          onClick={() => onSubmit(values)}
        >
          {submitLabel}
        </Action>
        <Action tone="quiet" size="l" stretched disabled={busy} onClick={onCancel}>
          {t('common.action.cancel')}
        </Action>
      </FormActions>
    </div>
  );
}
